import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import {
  PoPageModule,
  PoButtonModule,
  PoFieldModule,
  PoPageAction,
  PoDividerModule,
  PoNotificationService,
  PoWidgetModule,
  PoSelectOption,
  PoLoadingModule
} from '@po-ui/ng-components';

import { CNPJ_MASK, CNPJ_PATTERN, isValidCnpj } from '../../core/utils/validators';
import {
  NotasAdicionarService,
  GrupoPayload,
  TipoProcesso,
  CnaeOption
} from './notas-adicionar.service';
import { AnexosService, AnexoTipo, GedFolderCheck, gedIndisponivelMsg } from '../nota-detalhe/anexos.service';
import { toIsoDateLocal } from '../../shared/date-utils';
import { NotaDetalheService } from '../nota-detalhe/nota-detalhe.service';
import { DictsOcultoService, DictsOculto, DictOption, DictProdutoOculto } from '../nota-detalhe/dicts-oculto.service';
import { DictsSeriesService } from '../nota-detalhe/dicts-series.service';
import { FilialService, Filial } from '../../core/services/filial.service';
import { PdfParserService, PdfSemTextoError } from './pdf-parser/pdf-parser.service';
import { NfseData, sumRetencoes } from './pdf-parser/nfse-parser';
import { isChaveDacteValida } from './pdf-parser/cte-parser';
import { tomadorEntreFiliais } from './pdf-parser/tomador-filial';
import { casarCodigoServico } from './pdf-parser/codigo-servico';
import { CteTesOpcao } from '../nota-detalhe/nota-detalhe.service';

type AnexoSlot = 'danfe' | 'boleto' | 'romaneio';

interface DocumentoForm {
  processo: string;
  numeroNf: string;
  serie: string;
  // Chave de acesso do CT-e (44 digitos). Extraida do DACTE pelo cte-parser
  // (editavel e obrigatoria no CTe). Vazia para NFSe.
  chaveAcesso: string;
  emissao: string;        // ISO yyyy-mm-dd
  valor: number | null;
  vencimento: string;     // ISO yyyy-mm-dd
  fin: boolean;
  // Indica se a NF tem algum tipo de retencao (federal, INSS ou ISS).
  // Sinal generico definido pelo usuario na linha do documento -
  // backend usa esse flag como lOutRet em U_PnfTesNF.
  retencao: boolean;
  // Valores de retencao extraidos do DANFE PDF (via pdf-parser). Enviados
  // ao backend pra U_PnfNatFin escolher a natureza correta. Zeros quando
  // nao houver retencao ou o parser nao identificar valores.
  valorIr: number;
  valorPis: number;
  valorCofins: number;
  valorCsll: number;
  valorInss: number;
  // Pedagio (CTe rodoviario): F1_VALPEDG no Protheus. Extraido via cte-parser
  // do DACTE quando o usuario anexa um PDF de CTe. Zero pra NFSe/sem CTe.
  valorPedagio: number;
  // Especie da nota (F1_ESPECIE): 'NFS' (NFSe, default) ou 'CTE' (CTe).
  // Setado automaticamente quando o parser detecta CTe pelo anexo.
  especie: 'NFS' | 'CTE';
  // Codigo da TES escolhida pelo usuario (D1_TES / F4_CODIGO). Para NFSe
  // o backend calcula via U_PnfTesNF; para CTe vem dessa selecao (planilha
  // REGRAS CTE).
  tipoOperacao: string;
  // Opcoes de TES para CTe (planilha REGRAS CTE) retornadas pelo
  // resolverProcesso quando o usuario digita o processo. So usadas quando
  // especie === 'CTE'.
  tesCteOpcoes: CteTesOpcao[];
  // Simples Nacional do emitente do CTe (auto via cte-parser, override
  // manual). Determina o subconjunto de TES (SIMPLES vs NORMAL).
  simplesNacional: boolean;
  // ICMS isento do CTe (CST 40/41/51, auto via cte-parser do PDF). O
  // autoPickTesCte prefere a TES ISENTO (003/004) quando true.
  icmsIsento: boolean;
  // status do processo (validado contra SW2/SW6 via resolverProcesso)
  processoEncerrado: boolean;
  dataEncerramentoProcesso: string;
  validandoProcesso: boolean;
  // Tipo de processo resolvido a partir do processo (SW2.W2_ZTPIMPO ->
  // encomenda/terceiros/propria). Mantido por documento porque cada NF
  // pode pertencer a processos com tipos distintos.
  tipoProcessoResolvido: string;
  // Fluxo fornecedor oculto: campos preenchidos pelo usuario quando o
  // grupo do CNPJ esta marcado como oculto. Substituem processo/CNAE.
  produto: string;
  contaContabil: string;
  bu: string;
  centroCusto: string;
  // Lista filtrada de produtos derivada da Conta Contabil escolhida
  // (cascata client-side - sem round-trip).
  produtoOptionsOculto: PoSelectOption[];
  // files
  danfeFile: File | null;
  boletoFile: File | null;
  romaneioFile: File | null;
  // drag state per slot
  danfeDragOver: boolean;
  boletoDragOver: boolean;
  romaneioDragOver: boolean;
  // ui state
  status: '' | 'enviando' | 'ok' | 'erro';
  mensagem: string;
}

interface GrupoForm {
  id: number;
  cnpjEmissor: string;
  fornecedorNome: string;
  servicoCnae: string;
  tipoProcesso: TipoProcesso | '';
  // Filial selecionada para este grupo (override do header global).
  // Default = filial corrente do FilialService.
  filialCnpj: string;
  // Pasta GED de destino dos anexos do grupo. Default = primeira da
  // lista (vinda do MV_PNFGFLD via AnexosService.gedFolders()).
  gedFolder: string;
  // Setado por onCnpjBlur a partir da resposta do backend (ZOE).
  // Quando true, libera o requisito de "processo obrigatorio" nos docs.
  fornecedorOculto: boolean;
  // Produto frete (CTe) para o grupo - substitui o servicoCnae quando o
  // grupo contem documentos CTe. Propagado para todos os docs CTe via
  // onProdutoCteChange. CNAE/Produto sao mutuamente exclusivos por grupo.
  produtoCte: string;
  documentos: DocumentoForm[];
  dragOver: boolean;
}

@Component({
  selector: 'app-notas-adicionar',
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoButtonModule,
    PoFieldModule,
    PoDividerModule,
    PoWidgetModule,
    PoLoadingModule
  ],
  templateUrl: './notas-adicionar.html',
  styleUrls: ['./notas-adicionar.css'],
})
export class NotasAdicionarComponent implements OnInit {

  grupos: GrupoForm[] = [];
  private nextId = 1;

  enviando = false;
  carregandoCnaes = false;

  cnaeOptions: PoSelectOption[] = [];
  filialOptions: PoSelectOption[] = [];
  gedFolderOptions: PoSelectOption[] = [];

  // Listas para fluxo de fornecedor oculto (carregadas uma vez).
  ocultoContaOptions: DictOption[] = [];
  ocultoBuOptions: DictOption[] = [];
  ocultoCcOptions: DictOption[] = [];
  private ocultoProdutos: DictProdutoOculto[] = [];

  readonly cnpjMask = CNPJ_MASK;
  readonly cnpjPattern = CNPJ_PATTERN;

  readonly tipoProcessoOptions: PoSelectOption[] = [
    { label: 'Terceiros', value: 'terceiros' },
    { label: 'Encomenda', value: 'encomenda' },
    { label: 'Propria',   value: 'propria'   }
  ];

  // Series aceitas na inclusao manual: carregadas de GET /portalnf/dicts/series
  // (parametro MV_PNFSER). Antes eram fixas S1/S2 aqui - ampliar as series
  // passou a ser configuracao do ERP, sem deploy do portal. A lista abaixo e
  // so o fallback usado ate a resposta chegar (ou se a chamada falhar) e
  // espelha o conteudo padrao do parametro.
  serieOptions: PoSelectOption[] = [
    { label: 'S1', value: 'S1' },
    { label: 'S2', value: 'S2' },
    { label: 'S3', value: 'S3' }
  ];

  // Produtos de frete para CTe (D1_COD). Mesma lista que U_PnfPrdCte
  // utiliza no backend - aqui exposto pra selecao manual quando o
  // usuario incluir um CTe na entrada manual.
  readonly produtosCteOptions: PoSelectOption[] = [
    { label: '000000000000263 - FRETE S/ VENDA',                  value: '000000000000263' },
    { label: '000000000020475 - FRETE S/ VENDA SIMPLES NACIONAL', value: '000000000020475' }
  ];

  // Tipos de processo aceitos pelo backend (W2_ZTPIMPO).
  readonly tipoProcessoCteOptions: PoSelectOption[] = [
    { label: 'Encomenda',     value: 'encomenda' },
    { label: 'Propria',       value: 'propria'   },
    { label: 'Conta e Ordem', value: 'terceiros' }
  ];

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Cadastrar',       action: () => this.cadastrar(),       icon: 'an an-floppy-disk' },
    { label: 'Adicionar Grupo', action: () => this.adicionarGrupo(),  icon: 'an an-plus' },
    { label: 'Voltar',          action: () => this.voltar(),          icon: 'an an-arrow-left' }
  ];

  constructor(
    private router: Router,
    private notif: PoNotificationService,
    private service: NotasAdicionarService,
    private anexosService: AnexosService,
    private notaDetalheService: NotaDetalheService,
    private dictsOcultoService: DictsOcultoService,
    private dictsSeriesService: DictsSeriesService,
    private filialService: FilialService,
    private pdfParser: PdfParserService
  ) {}

  ngOnInit(): void {
    this.carregarFiliais();
    this.carregarGedFolders();
    this.carregarCnaes();
    this.carregarDictsOculto();
    this.carregarSeries();
    // adicionarGrupo() depende de filialOptions/currentCnpj para o default,
    // entao espera o subscribe inicial do filiais$ disparar (que ja emite
    // sync o cache atual, mesmo vazio). Em paralelo dispara o loadAll.
    this.adicionarGrupo();
  }

  // ---------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------

  private carregarFiliais(): void {
    // Subscribe reativo: atualiza filialOptions sempre que a lista mudar
    // (loadAll resolve, ou alguem chamar set/clear). Tambem reaplica o
    // default nos grupos ja existentes que estavam sem filialCnpj.
    this.filialService.filiais$.subscribe(list => {
      this.filialOptions = list.map((f: Filial) => ({
        label: (f.cnpj ? f.cnpj + ' - ' : '') + f.nome,
        value: f.cnpj
      }));
      const fallback = this.filialService.currentCnpj() ?? (this.filialOptions[0]?.value as string | undefined);
      if (fallback) {
        for (const g of this.grupos) {
          if (!g.filialCnpj) {
            g.filialCnpj = fallback;
          }
        }
      }
    });
    // Dispara a carga (idempotente — se ja carregado, retorna cache).
    this.filialService.loadAll().subscribe({
      error: () => this.notif.warning('Nao foi possivel carregar a lista de filiais.')
    });
  }

  private carregarGedFolders(): void {
    this.anexosService.gedFolders().subscribe({
      next: (folders: string[]) => {
        this.gedFolderOptions = (folders ?? []).map(f => ({ label: f, value: f }));
        // Aplica o default nos grupos ja existentes (caso vagos)
        for (const g of this.grupos) {
          if (!g.gedFolder && this.gedFolderOptions.length > 0) {
            g.gedFolder = this.gedFolderOptions[0].value as string;
          }
        }
      },
      error: () => {
        this.notif.warning('Nao foi possivel carregar pastas GED.');
      }
    });
  }

  private carregarCnaes(): void {
    this.carregandoCnaes = true;
    this.service.cnaes().subscribe({
      next: list => {
        this.cnaeOptions = list.map((c: CnaeOption) => ({
          label: `${c.cnae} - ${c.descricao}`,
          value: c.cnae
        }));
        this.carregandoCnaes = false;
      },
      error: () => {
        this.notif.warning('Nao foi possivel carregar a lista de CNAEs.');
        this.carregandoCnaes = false;
      }
    });
  }

  // Dicionarios de fornecedor oculto (Conta Contabil, BU, CC, Produto).
  // Carrega 1x por sessao (cache no service); cada documento usa o helper
  // produtosPorConta para montar a cascata por Conta.
  private carregarDictsOculto(): void {
    this.dictsOcultoService.load().subscribe({
      next: (d: DictsOculto) => {
        this.ocultoContaOptions = d.contas;
        this.ocultoBuOptions    = d.bus;
        this.ocultoCcOptions    = d.centrosCusto;
        this.ocultoProdutos     = d.produtos;
        // Refresca cascatas de docs ja criados que tinham Conta selecionada.
        for (const g of this.grupos) {
          for (const d of g.documentos) {
            if (d.contaContabil) {
              d.produtoOptionsOculto = this.dictsOcultoService.produtosPorConta(this.ocultoProdutos, d.contaContabil);
            }
          }
        }
      },
      error: () => {
        // Silencioso - so afeta o fluxo oculto; usuario normal nao perde nada.
      }
    });
  }

  // Series da entrega (MV_PNFSER). Carrega 1x por sessao (cache no service).
  // Corrige os documentos ja criados que ficaram com uma serie fora da lista
  // configurada (o fallback pode diferir do parametro do ambiente). CTe nao
  // entra: usa a serie propria do documento, digitada pelo operador.
  private carregarSeries(): void {
    this.dictsSeriesService.load().subscribe({
      next: (series: DictOption[]) => {
        if (!series.length) { return; }
        this.serieOptions = series;
        for (const g of this.grupos) {
          for (const d of g.documentos) {
            if (d.especie !== 'CTE' && !series.some(s => s.value === d.serie)) {
              d.serie = this.serieDefault();
            }
          }
        }
      },
      error: () => {
        // Silencioso - mantem o fallback S1/S2/S3 ja carregado.
      }
    });
  }

  // Primeira serie da lista configurada: default de novos documentos.
  private serieDefault(): string {
    return (this.serieOptions[0]?.value as string) ?? 'S1';
  }

  // Cascata Conta -> Produto por documento (fornecedor oculto).
  onDocContaContabilChange(doc: DocumentoForm, value: string): void {
    doc.contaContabil = value ?? '';
    doc.produtoOptionsOculto = this.dictsOcultoService.produtosPorConta(this.ocultoProdutos, doc.contaContabil);
    if (doc.produto && !doc.produtoOptionsOculto.some(o => o.value === doc.produto)) {
      doc.produto = '';
    }
  }

  // ---------------------------------------------------------------------
  // Grupos / Documentos
  // ---------------------------------------------------------------------

  adicionarGrupo(): void {
    this.grupos.push({
      id: this.nextId++,
      cnpjEmissor: '',
      fornecedorNome: '',
      servicoCnae: '',
      tipoProcesso: '',
      filialCnpj: this.filialService.currentCnpj() ?? (this.filialOptions[0]?.value as string ?? ''),
      gedFolder: this.gedFolderOptions[0]?.value as string ?? '',
      fornecedorOculto: false,
      produtoCte: '',
      documentos: [],
      dragOver: false
    });
  }

  excluirGrupo(index: number): void {
    this.grupos.splice(index, 1);
  }

  adicionarDocumento(grupo: GrupoForm, danfeFile: File | null = null): void {
    const novo: DocumentoForm = {
      processo: '',
      // Pre-popula apenas se o nome do arquivo for puramente numerico
      // (ex: "12345.pdf"). Nomes como "NF ACPR 002626.pdf" sao ambiguos
      // demais para deduzir o numero da NF -> deixa vazio.
      numeroNf: this.extractNumeroFromFileName(danfeFile),
      serie: this.serieDefault(),
      chaveAcesso: '',
      emissao: this.todayIso(),
      valor: null,
      vencimento: '',
      fin: false,
      retencao: false,
      valorIr: 0,
      valorPis: 0,
      valorCofins: 0,
      valorCsll: 0,
      valorInss: 0,
      valorPedagio: 0,
      especie: 'NFS',
      tipoOperacao: '',
      tesCteOpcoes: [],
      icmsIsento: false,
      simplesNacional: false,
      processoEncerrado: false,
      dataEncerramentoProcesso: '',
      validandoProcesso: false,
      tipoProcessoResolvido: '',
      produto: '',
      contaContabil: '',
      bu: '',
      centroCusto: '',
      produtoOptionsOculto: [],
      danfeFile,
      boletoFile: null,
      romaneioFile: null,
      danfeDragOver: false,
      boletoDragOver: false,
      romaneioDragOver: false,
      status: '',
      mensagem: ''
    };
    grupo.documentos.push(novo);
    // Auto-preenchimento via parsing do DANFE quando o doc foi criado
    // ja com arquivo (caso drop direto no grupo).
    if (danfeFile) {
      void this.parseAndApplyDanfe(grupo, novo, danfeFile);
    }
  }

  // Data atual em ISO yyyy-mm-dd (default de emissao).
  private todayIso(): string {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  excluirDocumento(grupo: GrupoForm, idx: number): void {
    grupo.documentos.splice(idx, 1);
  }

  // ---------------------------------------------------------------------
  // Lookup do fornecedor por CNPJ (preenche o nome automaticamente)
  // ---------------------------------------------------------------------
  onCnpjBlur(grupo: GrupoForm): void {
    const cnpj = (grupo.cnpjEmissor || '').replace(/\D/g, '');
    if (!cnpj || !isValidCnpj(grupo.cnpjEmissor)) {
      return;
    }
    this.service.fornecedorPorCnpj(cnpj).subscribe({
      next: r => {
        // 200: SA2 ja tem o fornecedor
        if (r.exists && r.nome) {
          grupo.fornecedorNome = r.nome;
        }
        grupo.fornecedorOculto = !!r.oculto;
      },
      error: err => {
        // 404 com body = nao tem em SA2 mas a BrasilAPI sugeriu nome
        const body = err?.error;
        if (body?.nomeSugerido) {
          grupo.fornecedorNome = body.nomeSugerido;
          this.notif.information(`Fornecedor nao cadastrado. Sera criado como "${body.nomeSugerido}".`);
        }
        // Fornecedor novo (so existe em BrasilAPI) nunca eh oculto.
        grupo.fornecedorOculto = false;
        // outros erros nao mexem no nome (usuario digita manualmente)
      }
    });
  }

  // Tenta extrair o numero da NF a partir do nome do arquivo (DANFE).
  // Aceita apenas casos onde o "nome base" (sem extensao) eh PURAMENTE
  // numerico - ex: "000123456.pdf" -> "000123456". Nomes mistos como
  // "NF ACPR 002626.pdf" sao ambiguos demais -> retorna vazio para o
  // usuario digitar manualmente.
  private extractNumeroFromFileName(file: File | null): string {
    if (!file) return '';
    const base = file.name.replace(/\.[^/.]+$/, '').trim();
    return /^\d+$/.test(base) ? base : '';
  }

  // ---------------------------------------------------------------------
  // Validacao do processo (SW2/SW6.W6_DT_ENCE via resolverProcesso)
  // ---------------------------------------------------------------------

  // Chamado no (p-change) do input de processo (dispara no blur/enter).
  // Marca o doc como encerrado se SW6.W6_DT_ENCE estiver preenchido.
  // Esvaziar o processo limpa o flag automaticamente.
  onProcessoBlur(doc: DocumentoForm): void {
    const proc = (doc.processo || '').trim();
    doc.processoEncerrado       = false;
    doc.dataEncerramentoProcesso = '';
    doc.tipoProcessoResolvido   = '';
    if (!proc) {
      doc.validandoProcesso = false;
      return;
    }
    // Localiza grupo via reverse lookup pra acessar o filialCnpj (e converter
    // pra codigo Protheus). U_PnfTesCte (planilha REGRAS CTE) filtra TES por
    // codigo Protheus + sn, entao precisamos passar ambos.
    const grupo  = this.grupos.find(g => g.documentos.includes(doc));
    const codFil = grupo ? this.filialService.codigoByCnpj(grupo.filialCnpj) : '';
    doc.validandoProcesso = true;
    this.notaDetalheService.resolverProcesso({
      processo:     proc,
      filial:       codFil || undefined,
      sn:           doc.simplesNacional,
      // Override quando processo nao existir em SW2 (testes / processos
      // legados). Backend usa como fallback pra calcular as TES CTe.
      tipoProcesso: doc.tipoProcessoResolvido || undefined
    }).subscribe({
      next: r => {
        doc.validandoProcesso = false;
        // tesCteOpcoes sempre eh populado, mesmo quando o processo nao
        // existe em SW2 - o backend cai em tipoProcesso='' => 'C' (CONTA
        // E ORDEM) e devolve as TES de C para a filial. Sem isso, o
        // select fica vazio em casos de teste com processo ficticio.
        doc.tesCteOpcoes = r.tesCteOpcoes ?? [];
        if (r.processoExiste === false) {
          this.notif.warning(`Processo ${proc} nao localizado no Protheus.`);
          return;
        }
        // Captura tipo do processo (encomenda/terceiros/propria) que sera
        // enviado no payload por documento - elimina o combo manual.
        doc.tipoProcessoResolvido = r.tipoProcesso ?? '';
        if (r.processoEncerrado) {
          doc.processoEncerrado       = true;
          doc.dataEncerramentoProcesso = r.dataEncerramento ?? '';
          const dt = doc.dataEncerramentoProcesso
            ? ` (encerrado em ${this.formatIsoBr(doc.dataEncerramentoProcesso)})`
            : '';
          this.notif.error(`Processo ${proc} encerrado${dt}. Nao sera possivel incluir a nota.`);
        }
      },
      error: () => {
        doc.validandoProcesso = false;
        this.notif.warning('Nao foi possivel validar o processo agora.');
      }
    });
  }

  // Handler do toggle "Simples Nacional" do documento (CTe). Atualiza o
  // produto frete automaticamente conforme o regime e re-resolve a TES.
  onSimplesNacionalChange(doc: DocumentoForm, v: boolean): void {
    doc.simplesNacional = v;
    if (doc.especie === 'CTE') {
      doc.produto = this.produtoCtePorSn(v);
      this.refreshTesCte(doc);
    }
  }

  // Handler do toggle "Gera Financeiro". Para CTe, re-escolhe a TES
  // baseada no novo valor de dup. Reset doc.tipoOperacao pra forcar
  // o autoPickTesCte a re-escolher (em vez de preservar a anterior).
  onFinChange(doc: DocumentoForm, v: boolean): void {
    doc.fin = v;
    // Sem financeiro: limpa vencimento + boleto anexado (regra 2026-05-29).
    if (!v) {
      doc.vencimento = '';
      doc.boletoFile = null;
    }
    if (doc.especie === 'CTE') {
      doc.tipoOperacao = '';
      this.autoPickTesCte(doc);
    }
  }

  // Handler do select "Tipo de Processo" do documento CTe.
  onTipoProcessoCteChange(doc: DocumentoForm, v: string): void {
    doc.tipoProcessoResolvido = v;
    if (doc.especie === 'CTE') { this.refreshTesCte(doc); }
  }

  // Refresca a lista de TES de CTe (planilha REGRAS CTE). Funciona mesmo
  // sem processo preenchido - quando vazio, envia um placeholder pro
  // backend e usa o override de tipoProcesso/sn pra filtrar as opcoes.
  // Apos receber, auto-escolhe a TES via autoPickTesCte.
  private refreshTesCte(doc: DocumentoForm): void {
    if (doc.especie !== 'CTE') { return; }
    const grupo  = this.grupos.find(g => g.documentos.includes(doc));
    const codFil = grupo ? this.filialService.codigoByCnpj(grupo.filialCnpj) : '';
    if (!codFil) {
      doc.tesCteOpcoes = [];
      return;
    }
    const proc = (doc.processo || '').trim() || '_placeholder_';
    this.notaDetalheService.resolverProcesso({
      processo:     proc,
      filial:       codFil,
      sn:           doc.simplesNacional,
      tipoProcesso: doc.tipoProcessoResolvido || undefined
    }).subscribe({
      next: r => {
        doc.tesCteOpcoes = r.tesCteOpcoes ?? [];
        this.autoPickTesCte(doc);
      },
      error: () => {
        doc.tesCteOpcoes = [];
      }
    });
  }

  // Escolhe automaticamente a TES de CTe como SUGESTAO inicial. So aplica
  // se o usuario ainda nao escolheu manualmente uma TES valida na lista
  // (preserva a escolha do usuario quando ele troca SN/processo).
  // Criterios (em ordem):
  //   1. presenca de "PEDAGIO" na descricao combina com valorPedagio
  //   2. dup === doc.fin (gera duplicata combina com Gera Financeiro atual)
  //   3. primeiro candidato restante
  // Auto-escolhe a TES de CTe definitiva, baseado no contexto do doc
  // (fin + valorPedagio + descricao). O operador NAO escolhe TES de CTe -
  // o template trava o select e mostra somente a opcao calculada aqui.
  //
  // Heuristica (em ordem):
  //   1) Pedagio: valorPedagio>0 -> so TES com "PEDAGIO" no desc; senao,
  //      so TES sem "PEDAGIO" (evita pegar TES de pedagio sem pedagio real)
  //   2) Gera Financeiro: filtra por dup === doc.fin
  //   3) Prefere TES com marcador C/FINANCEIRO ou S/FINANCEIRO no desc
  //      consistente com o ramo dup
  //   4) Exclui TES com "SEM ICMS"/"S/ICMS" (excecoes) se houver outras
  //   5) Pega a primeira da lista filtrada
  private autoPickTesCte(doc: DocumentoForm): void {
    if (doc.especie !== 'CTE' || !doc.tesCteOpcoes?.length) {
      return;
    }
    let candidatos = doc.tesCteOpcoes.slice();

    const temPedagio = (doc.valorPedagio ?? 0) > 0;
    const comPedagio = candidatos.filter(o => /pedagio/i.test(o.desc));
    const semPedagio = candidatos.filter(o => !/pedagio/i.test(o.desc));
    if (temPedagio && comPedagio.length > 0) { candidatos = comPedagio; }
    else if (!temPedagio && semPedagio.length > 0) { candidatos = semPedagio; }

    const dupMatch = candidatos.filter(o => o.dup === doc.fin);
    if (dupMatch.length === 0) {
      // Nenhuma TES com dup compativel com doc.fin: NAO sobrescreve a
      // escolha do user, zera TES, avisa.
      doc.tipoOperacao = '';
      this.notif.warning(
        `Sem TES de CTe disponivel para ${doc.fin ? '"com financeiro"' : '"sem financeiro"'} ` +
        `nesse processo - reveja o cadastro.`
      );
      return;
    }
    candidatos = dupMatch;

    const explicitFin = candidatos.filter(o =>
      doc.fin ? /c\/\s*financeiro/i.test(o.desc) : /s\/\s*financeiro/i.test(o.desc)
    );
    if (explicitFin.length > 0) { candidatos = explicitFin; }

    // ICMS: isento (CST 40/41/51 detectado do PDF) -> prefere TES ISENTO
    // (003/004); senao -> exclui isentas junto com S/ICMS, mantem C/ICMS.
    // Paridade com o autoPickTesCte do nota-detalhe (fluxo automatico).
    if (doc.icmsIsento) {
      const isentoOpts = candidatos.filter(o => /isento/i.test(o.desc));
      if (isentoOpts.length > 0) { candidatos = isentoOpts; }
    } else {
      const semIcmsExc = candidatos.filter(o => !/sem\s*icms|s\/\s*icms|isento/i.test(o.desc));
      if (semIcmsExc.length > 0) { candidatos = semIcmsExc; }
    }

    const escolhida = candidatos[0];
    doc.tipoOperacao = escolhida.tes;
    // doc.fin ja eh consistente com escolhida.dup (filtramos por dupMatch)
    doc.retencao = !/sem\s+icms|s\/\s*icms/i.test(escolhida.desc);
  }

  // Opcoes do select TES pra CTe: lista completa das tesCteOpcoes
  // (operador pode editar - regra atualizada 2026-05-27). O autoPickTesCte
  // sugere a inicial.
  getTesCteOptions(doc: DocumentoForm): PoSelectOption[] {
    if (doc.especie !== 'CTE' || !doc.tesCteOpcoes?.length) {
      return [{ label: 'Preencha o processo para carregar a TES', value: '' }];
    }
    return doc.tesCteOpcoes.map(o => ({
      label: `(${o.tes}) ${o.desc}`,
      value: o.tes
    }));
  }

  // Handler do select Tipo de Operacao (TES) para CTe. Espelha 1 flag:
  //   - "Retencao" conforme descricao: TES com "SEM ICMS" = sem retencao;
  //     demais = com retencao (CTe normal tem ICMS na nota)
  //
  // NAO sobrescreve doc.fin (Gera Financeiro) - regra atualizada 2026-05-29.
  // Antes virava o switch do user pra TES.dup, mas isso disparava validacao
  // de boleto/vencimento errada quando o user marcou explicitamente. O
  // switch agora reflete sempre o que o user escolheu; inconsistencia TES
  // vs fin fica visivel, backend valida.
  onTipoOperacaoCteChange(doc: DocumentoForm, v: string): void {
    doc.tipoOperacao = v;
    const opcao = doc.tesCteOpcoes?.find(o => o.tes === v);
    if (opcao) {
      // Retencao: TES com "SEM ICMS" / "S/ICMS" na descricao = sem ICMS na
      // nota = sem retencao. Demais TES tem ICMS implicito = retencao Sim.
      const semIcms = /sem\s+icms|s\/\s*icms/i.test(opcao.desc);
      doc.retencao = !semIcms;
    }
  }

  private formatIsoBr(iso: string): string {
    if (!iso || iso.length < 10) return iso;
    return iso.substring(8, 10) + '/' + iso.substring(5, 7) + '/' + iso.substring(0, 4);
  }

  // ---------------------------------------------------------------------
  // Drag-and-drop e file inputs
  // ---------------------------------------------------------------------

  onDragOver(event: DragEvent, grupo: GrupoForm): void {
    event.preventDefault();
    event.stopPropagation();
    grupo.dragOver = true;
  }

  onDragLeave(event: DragEvent, grupo: GrupoForm): void {
    event.preventDefault();
    grupo.dragOver = false;
  }

  onDrop(event: DragEvent, grupo: GrupoForm): void {
    event.preventDefault();
    grupo.dragOver = false;
    const files = event.dataTransfer?.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f.size > AnexosService.MAX_BYTES) {
        this.notif.warning(`${f.name}: excede 10 MB. Ignorado.`);
        continue;
      }
      this.adicionarDocumento(grupo, f);
    }
  }

  onFileSelected(event: Event, doc: DocumentoForm, slot: AnexoSlot): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.assignFile(doc, slot, input.files[0]);
    input.value = '';
  }

  onSlotDragOver(event: DragEvent, doc: DocumentoForm, slot: AnexoSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragFlag(doc, slot, true);
  }

  onSlotDragLeave(event: DragEvent, doc: DocumentoForm, slot: AnexoSlot): void {
    event.preventDefault();
    this.setDragFlag(doc, slot, false);
  }

  onSlotDrop(event: DragEvent, doc: DocumentoForm, slot: AnexoSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragFlag(doc, slot, false);
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    this.assignFile(doc, slot, files[0]);
  }

  clearFile(doc: DocumentoForm, slot: AnexoSlot): void {
    if (slot === 'danfe')    doc.danfeFile = null;
    if (slot === 'boleto')   doc.boletoFile = null;
    if (slot === 'romaneio') doc.romaneioFile = null;
  }

  getFile(doc: DocumentoForm, slot: AnexoSlot): File | null {
    if (slot === 'danfe')    return doc.danfeFile;
    if (slot === 'boleto')   return doc.boletoFile;
    if (slot === 'romaneio') return doc.romaneioFile;
    return null;
  }

  private assignFile(doc: DocumentoForm, slot: AnexoSlot, file: File): void {
    if (file.size > AnexosService.MAX_BYTES) {
      this.notif.warning(`${file.name}: excede 10 MB.`);
      return;
    }
    if (slot === 'danfe')    doc.danfeFile = file;
    if (slot === 'boleto')   doc.boletoFile = file;
    if (slot === 'romaneio') doc.romaneioFile = file;
    // Auto-preenchimento via parsing do DANFE - so dispara no slot 'danfe'.
    // Localiza o grupo via referencia circular (procura o doc na lista).
    if (slot === 'danfe') {
      const grupo = this.grupos.find(g => g.documentos.includes(doc));
      if (grupo) void this.parseAndApplyDanfe(grupo, doc, file);
    }
  }

  // ---------------------------------------------------------------------
  // Auto-preenchimento via parsing do DANFE PDF
  // ---------------------------------------------------------------------

  // Le o PDF, extrai os campos NFSe e aplica no grupo/documento. Best-
  // effort: se algo falhar, mantem o anexo e mostra toast discreto.
  // Fornecedor oculto (ZOE): aborta o auto-fill apos lookup do CNPJ.
  private async parseAndApplyDanfe(grupo: GrupoForm, doc: DocumentoForm, file: File): Promise<void> {
    let data: NfseData;
    try {
      data = await this.pdfParser.parseDanfe(file);
    } catch (e) {
      // PDF de imagem (nota digitalizada) e' caso a parte: nao adianta tentar
      // de novo nem trocar o layout, e o aviso precisa cobrar as retencoes
      // pelo mesmo motivo do bloco abaixo - retencao zerada nao e' neutra.
      if (e instanceof PdfSemTextoError) {
        this.notif.warning(
          'Este PDF nao tem texto - e uma imagem digitalizada. Preencha os ' +
          'dados da nota E as retencoes (IR/PIS/COFINS/CSLL/INSS) a mao - ' +
          'sem elas a natureza sai como "sem retencao".'
        );
      } else {
        this.notif.warning('Nao foi possivel ler o PDF do DANFE. Preencha os campos manualmente.');
      }
      return;
    }

    // Sem CNPJ extraido -> o PDF nao tem camada de texto legivel (fonte sem
    // ToUnicode: o pdf.js devolve glifos embaralhados) ou o layout e' novo.
    // Nesse caso NENHUMA retencao foi lida - e retencao zerada nao e' neutra:
    // o backend classifica a natureza por elas (U_PnfNatFin), entao uma nota
    // com IRRF entra como 253012 "sem retencao" e o titulo sai sem IR.
    // Caso real: NF 5529 (Itajai), IRRF 73,30 -> deveria ser 253014.
    // Por isso o aviso cita explicitamente as retencoes.
    if (!data.cnpjEmissor) {
      this.notif.warning(
        'Nao foi possivel ler o PDF (texto ilegivel). Preencha manualmente ' +
        'os dados da nota E as retencoes (IR/PIS/COFINS/CSLL/INSS) - sem ' +
        'elas a natureza sai como "sem retencao".'
      );
      this.applyDanfeToDoc(doc, data, /*incluirRetencao*/ true);
      return;
    }

    // Lookup ZOE: se oculto, nao auto-preenche os campos da nota (regra de
    // negocio: auto-fill so vale pra fornecedor normal). Excecao: as
    // retencoes, que definem a natureza e por isso nao sao dispensaveis.
    this.service.fornecedorPorCnpj(data.cnpjEmissor).subscribe({
      next: r => {
        if (r.oculto) {
          // Fornecedor oculto nao tem auto-preenchimento dos campos da nota
          // (regra de negocio) - MAS as retencoes sao aplicadas mesmo assim.
          // Retencao nao e "preenchimento de campo": e o que o backend usa
          // para classificar a natureza (U_PnfNatFin), e a natureza e o
          // UNICO vetor da retencao no titulo (flags ED_CALC* da SED). Sem
          // isso a nota entra com 253012 "sem retencao" e o titulo sai
          // cheio, sem forma de o operador corrigir - a tela so oferece o
          // switch "Retencao" (booleano), que nao distingue 253016 de
          // 253025/253026/253014. Causa das NF 251 e 252 (PAINEL MAXX TAX).
          this.notif.information('Fornecedor oculto - preencha os campos manualmente.');
          grupo.fornecedorOculto = true;
          this.applyRetencoesToDoc(doc, data);
          return;
        }
        this.applyDanfeToGrupo(grupo, data, r.nome);
        this.applyDanfeToDoc(doc, data, /*incluirRetencao*/ true);
        this.syncProdutoCteGrupo(grupo, doc);
      },
      error: () => {
        // Fornecedor novo (so brasilAPI / nao em SA2): trata como normal
        // e aplica preenchimento. Backend criara o fornecedor.
        this.applyDanfeToGrupo(grupo, data, undefined);
        this.applyDanfeToDoc(doc, data, /*incluirRetencao*/ true);
        this.syncProdutoCteGrupo(grupo, doc);
      }
    });
  }

  // Sincroniza grupo.produtoCte com o produto do primeiro doc CTe.
  // Permite o select de Produto Frete (nivel grupo) refletir o auto-fill
  // feito por applyDanfeToDoc.
  private syncProdutoCteGrupo(grupo: GrupoForm, doc: DocumentoForm): void {
    if (doc.especie === 'CTE' && doc.produto && !grupo.produtoCte) {
      grupo.produtoCte = doc.produto;
    }
  }

  // Aplica campos do parser no grupo (CNPJ emissor, nome, CNAE, filial).
  // So sobrescreve se o usuario ainda nao tiver mexido (evita overwrite manual).
  private applyDanfeToGrupo(grupo: GrupoForm, data: NfseData, nomeSA2?: string): void {
    if (data.cnpjEmissor && !grupo.cnpjEmissor) {
      // Aplica com mascara padrao XX.XXX.XXX/XXXX-XX
      grupo.cnpjEmissor = this.formatCnpj(data.cnpjEmissor);
      // Dispara lookup fornecedor (popula nome se ainda nao foi setado)
      this.onCnpjBlur(grupo);
    }
    if ((nomeSA2 ?? data.nomeEmissor) && !grupo.fornecedorNome) {
      grupo.fornecedorNome = (nomeSA2 ?? data.nomeEmissor ?? '').toUpperCase();
    }
    // CNAE: aplica se houver match na lista de opcoes. Tenta variacoes
    // (com/sem ponto) pra cobrir as formas que aparecem no PDF.
    if (data.cnaeServico && !grupo.servicoCnae) {
      const cnaeMatch = this.matchCnaeOption(data.cnaeServico);
      if (cnaeMatch) grupo.servicoCnae = cnaeMatch;
    }
    // Filial: identifica pelo CNPJ do tomador da NF (Ascensus). Match
    // contra filialOptions (carregadas de M0_CGC pelo FilialService).
    // tomadorEntreFiliais e a mesma regra do simulador de regras - cobre o
    // DACTE em que o rotulo do tomador nao aponta o CNPJ certo.
    const tomador = tomadorEntreFiliais(data, this.filialOptions.map(o => String(o.value)));
    if (tomador) {
      const filMatch = this.matchFilialByCnpj(tomador);
      if (filMatch) grupo.filialCnpj = filMatch;
    }
  }

  // Casa o CNPJ tomador (14 digitos) com uma das filialOptions disponiveis.
  // Compara so digitos pra ignorar diferencas de mascara.
  private matchFilialByCnpj(cnpjTomador: string): string {
    if (!cnpjTomador) return '';
    const target = cnpjTomador.replace(/\D/g, '');
    const opt = this.filialOptions.find(o => {
      const v = String(o.value).replace(/\D/g, '');
      return v === target;
    });
    return opt ? String(opt.value) : '';
  }

  // Aplica no documento SO as retencoes lidas do PDF (IR/PIS/COFINS/CSLL/
  // INSS) e o flag generico. Separado de applyDanfeToDoc porque o fluxo de
  // fornecedor oculto nao auto-preenche numero/valor/processo, mas precisa
  // das retencoes: o backend escolhe a natureza por elas (U_PnfNatFin) e e
  // a natureza que dispara a retencao no titulo.
  private applyRetencoesToDoc(doc: DocumentoForm, data: NfseData): void {
    doc.valorIr     = data.retencoes.ir;
    doc.valorPis    = data.retencoes.pis;
    doc.valorCofins = data.retencoes.cofins;
    doc.valorCsll   = data.retencoes.csll;
    doc.valorInss   = data.retencoes.inss;
    // Flag generico: marca se soma > 0.
    doc.retencao = sumRetencoes(data.retencoes) > 0;
  }

  // Aplica campos do parser no documento (numero, valor, processo, retencoes).
  private applyDanfeToDoc(doc: DocumentoForm, data: NfseData, incluirRetencao: boolean): void {
    if (data.numeroNf && !doc.numeroNf) doc.numeroNf = data.numeroNf;
    if (data.valorTotal && data.valorTotal > 0 && !doc.valor) doc.valor = data.valorTotal;
    // Emissao: regra do usuario - sempre data atual, ele edita se quiser.
    // Ja vem preenchido por adicionarDocumento(); nao sobrescreve aqui.

    if (data.processo && !doc.processo) {
      doc.processo = data.processo;
      // Dispara validacao SW2/SW6 (popula tipoProcessoResolvido).
      this.onProcessoBlur(doc);
    }

    if (incluirRetencao) {
      this.applyRetencoesToDoc(doc, data);
    }
    // Pedagio (CTe rodoviario): backend deduz do D1_TOTAL e grava em
    // F1_VALPEDG. Se zero/ausente, nao impacta o calculo.
    if (data.valorPedagio && data.valorPedagio > 0) {
      doc.valorPedagio = data.valorPedagio;
    }
    // Quando o anexo eh CTe, marca a especie pro backend gravar F1_ESPECIE='CTE'.
    // simplesNacional vem do cte-parser (heuristica de texto "Simples Nacional");
    // usuario pode sobrescrever via toggle na UI antes de cadastrar.
    if (data.source === 'cte') {
      doc.especie = 'CTE';
      // Chave de acesso (44 dig) extraida do DACTE. serie e numero vem
      // embutidos nela e sao autoritativos quando a chave foi validada.
      doc.chaveAcesso = data.chaveAcesso || '';
      if (data.chaveAcesso) {
        if (data.serie)    { doc.serie = data.serie; }
        if (data.numeroNf) { doc.numeroNf = data.numeroNf; }
      }
      doc.simplesNacional = !!data.simplesNacional;
      // ICMS isento (CST 40/41/51 lido do DACTE): direciona o autoPickTesCte
      // para a TES ISENTO (003/004). Heuristica de PDF - operador confere.
      doc.icmsIsento = !!data.icmsIsento;
      // CTe gera financeiro por default (transportadora a pagar). Usuario
      // pode desligar manualmente em casos especiais. Esse default tambem
      // direciona a auto-escolha da TES (dup=Sim).
      doc.fin = true;
      // CTe normal tem ICMS (retencao Sim). Refinado pelo autoPickTesCte
      // conforme a descricao da TES escolhida ("SEM ICMS" desliga).
      doc.retencao = true;
      // Auto-seleciona o produto frete pelo regime do emitente: Simples
      // Nacional -> 000000000020475 ; Normal -> 000000000000263. Usuario
      // pode trocar via select se precisar.
      doc.produto = this.produtoCtePorSn(doc.simplesNacional);
      // Pre-popula a lista de TES com o tipoProcesso default 'encomenda'
      // (mais comum em CTes do dia-a-dia). Usuario pode trocar via select.
      if (!doc.tipoProcessoResolvido) {
        doc.tipoProcessoResolvido = 'encomenda';
      }
      this.refreshTesCte(doc);
    }
  }

  // Feedback do campo Chave de Acesso do CTe: informa se a chave digitada/
  // extraida e valida (44 digitos + DV mod-11) ou nao. Usado no [p-help].
  chaveCteHelp(doc: DocumentoForm): string {
    const d = (doc.chaveAcesso || '').replace(/\D/g, '');
    if (!d) { return 'Chave nao extraida do PDF - informe os 44 digitos.'; }
    if (d.length !== 44) { return 'Chave incompleta (' + d.length + '/44 digitos).'; }
    return isChaveDacteValida(d)
      ? 'Chave valida (DV confere).'
      : 'Chave invalida - digito verificador nao confere. Confira os numeros.';
  }

  // Mapeia o regime do emitente para o codigo de produto frete (D1_COD).
  // Mesma logica que U_PnfPrdCte aplica no backend.
  private produtoCtePorSn(sn: boolean): string {
    return sn ? '000000000020475' : '000000000000263';
  }

  // Indica se o grupo possui pelo menos 1 documento CTe. Usado pra trocar
  // o select de CNAE pelo de Produto Frete no nivel do grupo.
  grupoTemCte(grupo: GrupoForm): boolean {
    return grupo.documentos.some(d => d.especie === 'CTE');
  }

  // Handler do select Produto Frete (nivel grupo). Propaga pra todos os
  // documentos CTe do grupo - cada D1_COD vai per-doc no payload.
  onProdutoCteGrupoChange(grupo: GrupoForm, v: string): void {
    grupo.produtoCte = v;
    for (const d of grupo.documentos) {
      if (d.especie === 'CTE') {
        d.produto = v;
      }
    }
  }

  // Tenta casar a string extraida do PDF com uma opcao da lista de CNAEs
  // (cnaeOptions). Suporta variacoes: "20.01.01", "200101", "20.01" etc.
  // Mesma regra do simulador de regras (casarCodigoServico).
  private matchCnaeOption(raw: string): string {
    return casarCodigoServico(raw, this.cnaeOptions.map(o => String(o.value)));
  }

  // Formata 14 digitos -> XX.XXX.XXX/XXXX-XX
  private formatCnpj(d: string): string {
    const x = d.replace(/\D/g, '').padStart(14, '0').substring(0, 14);
    return `${x.substring(0,2)}.${x.substring(2,5)}.${x.substring(5,8)}/${x.substring(8,12)}-${x.substring(12,14)}`;
  }

  private setDragFlag(doc: DocumentoForm, slot: AnexoSlot, value: boolean): void {
    if (slot === 'danfe')    doc.danfeDragOver = value;
    if (slot === 'boleto')   doc.boletoDragOver = value;
    if (slot === 'romaneio') doc.romaneioDragOver = value;
  }

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  async cadastrar(): Promise<void> {
    if (this.enviando) return;
    if (!this.validar()) return;

    this.enviando = true;

    // Gate GED: cada doc com anexo precisa ter a pasta GED ja criada no
    // Conexos - senao o upload pos-cadastro falha silenciosamente (anexo
    // orfao). Decisao: cadastra os docs com pasta OK e PULA os com pasta
    // faltando (marca o doc e dropa grupo que ficar vazio). Pula docs sem
    // processo (GED-por-processo nao se aplica) e sem anexo. Pasta vem do
    // grupo (g.gedFolder); filial via g.filialCnpj (backend resolve U_RetFil).
    const cacheGed = new Map<string, GedFolderCheck>();
    const bloqueados = new Set<DocumentoForm>();
    for (const g of this.grupos) {
      for (const d of g.documentos) {
        const temAnexo = !!(d.danfeFile || d.boletoFile || d.romaneioFile);
        if (!temAnexo || !d.processo || !g.gedFolder) { continue; }
        const key = `${g.filialCnpj}|${d.processo}|${g.gedFolder}`;
        let check = cacheGed.get(key);
        if (check === undefined) {
          try {
            check = await firstValueFrom(this.anexosService.gedFolderExists({
              cnpjFilial: g.filialCnpj, processo: d.processo, folder: g.gedFolder
            }));
          } catch { check = { exists: false, gedStatus: 'OFFLINE' }; }
          cacheGed.set(key, check);
        }
        // GED inacessivel: aborta o cadastro inteiro em vez de marcar cada doc
        // com "pasta inexistente" - a pasta pode existir e o erro e de acesso.
        if (check.gedStatus !== 'OK') {
          this.enviando = false;
          this.notif.error(gedIndisponivelMsg(check.gedStatus)!);
          return;
        }
        if (!check.exists) {
          bloqueados.add(d);
          d.status   = 'erro';
          d.mensagem = `Pasta GED "${g.gedFolder}" inexistente no Conexos - crie a pasta antes de cadastrar.`;
        }
      }
    }
    if (bloqueados.size > 0) {
      this.notif.warning(`${bloqueados.size} documento(s) nao serao cadastrados - pasta GED inexistente no Conexos.`);
    }

    // So' grupos com ao menos 1 doc liberado entram no payload (dropa grupo
    // vazio). gruposLib x docs-liberados definem a ordem usada no mapeamento
    // de resp.results abaixo.
    const gruposLib = this.grupos.filter(g => g.documentos.some(d => !bloqueados.has(d)));
    if (gruposLib.length === 0) {
      this.enviando = false;
      this.notif.warning('Nenhum documento liberado para cadastro (pasta GED faltando). Crie as pastas no Conexos.');
      return;
    }

    const payload: GrupoPayload[] = gruposLib.map(g => ({
      cnpjEmissor:    g.cnpjEmissor.replace(/\D/g, ''),
      fornecedorNome: g.fornecedorNome || undefined,
      // CNAE pode vir vazio em fluxo oculto (servico dispensado).
      servicoCnae:    g.servicoCnae,
      // Fallback: se nenhum doc tem tipoProcessoResolvido, usa 'terceiros'
      tipoProcesso:   (g.tipoProcesso || 'terceiros') as TipoProcesso,
      // Filial selecionada no front - backend usa U_RetFil(cnpjFilial).
      // Se vier vazio o backend cai pro header CNPJ (filial atual do toolbar).
      cnpjFilial:     g.filialCnpj || undefined,
      documentos: g.documentos.filter(d => !bloqueados.has(d)).map(d => ({
        processo:   d.processo || undefined,
        numeroNf:   d.numeroNf,
        serie:      d.serie,
        chaveAcesso: d.chaveAcesso || undefined,
        emissao:    d.emissao,
        valor:      Number(d.valor),
        vencimento: toIsoDateLocal(d.vencimento) || undefined,
        fin:        d.fin,
        retencao:   d.retencao,
        // Valores de retencao (extraidos do DANFE ou zero). Backend usa em
        // U_PnfNatFin pra classificar F1_NATUREZ. Envia sempre, mesmo
        // zerados, pra ficar explicito o calculo na rota.
        valorIr:     d.valorIr     || undefined,
        valorPis:    d.valorPis    || undefined,
        valorCofins: d.valorCofins || undefined,
        valorCsll:   d.valorCsll   || undefined,
        valorInss:   d.valorInss   || undefined,
        valorPedagio: d.valorPedagio || undefined,
        // Envia 'CTE' so quando o parser detectou; senao deixa o backend
        // usar o default 'NFS' (omite a propriedade quando 'NFS').
        especie: d.especie === 'CTE' ? 'CTE' : undefined,
        // Para CTe: tipoOperacao (TES) e simplesNacional. Backend pula o
        // calculo U_PnfTesNF e usa o TES direto, alem de chamar U_PnfPrdCte
        // pra resolver o produto frete por regime.
        tipoOperacao:    d.especie === 'CTE' ? (d.tipoOperacao || undefined) : undefined,
        simplesNacional: d.especie === 'CTE' ? d.simplesNacional : undefined,
        // Tipo de processo per-doc resolvido via resolverProcesso (sobrescreve
        // o do grupo no backend quando preenchido).
        tipoProcesso: (d.tipoProcessoResolvido || undefined) as TipoProcesso | undefined,
        // Fluxo fornecedor oculto: campos manuais (sem processo/CNAE).
        // Backend detecta via ZOE; quando nao for oculto, esses campos
        // sao ignorados.
        // Para CTe, doc.produto tambem eh enviado (override do default
        // U_PnfPrdCte via select de produto frete na UI).
        produto:        (g.fornecedorOculto || d.especie === 'CTE') ? (d.produto || undefined) : undefined,
        contaContabil:  g.fornecedorOculto ? (d.contaContabil  || undefined) : undefined,
        bu:             g.fornecedorOculto ? (d.bu             || undefined) : undefined,
        centroCusto:    g.fornecedorOculto ? (d.centroCusto    || undefined) : undefined
      }))
    }));

    try {
      const resp = await firstValueFrom(this.service.adicionar(payload));

      // Mapeia resultados -> docs enviados (mesma ordem do payload:
      // gruposLib x docs liberados; os bloqueados ja foram marcados acima).
      let idx = 0;
      for (const g of gruposLib) {
        for (const d of g.documentos.filter(dd => !bloqueados.has(dd))) {
          const r = resp.results[idx++];
          if (!r) continue;
          if (r.ok && r.chave) {
            d.status   = 'enviando';
            d.mensagem = 'Enviando anexos...';
            const anexosOk = await this.uploadAnexos(d, r.chave, g.gedFolder, g.servicoCnae);
            // Serie efetivada: o backend pode ter escalonado a serie pedida
            // (S1 ocupada -> S2). Reflete na tela para o operador identificar
            // qual documento foi criado.
            d.serie = r.chave.serie;
            if (anexosOk) {
              d.status   = 'ok';
              d.mensagem = `OK (NF ${r.chave.numero} - série ${r.chave.serie})` +
                           (r.aviso ? ` ${r.aviso}` : '');
            }
          } else {
            d.status   = 'erro';
            d.mensagem = r.erro ?? 'Erro';
          }
        }
      }

      if (resp.totalErr === 0) {
        this.notif.success(`${resp.totalOk} nota(s) cadastrada(s).`);
      } else if (resp.totalOk === 0) {
        this.notif.error(`Falha em todas as notas (${resp.totalErr}).`);
      } else {
        this.notif.warning(`Cadastro parcial: ${resp.totalOk} ok, ${resp.totalErr} erro.`);
      }
    } catch (err: any) {
      const msg = err?.error?.message ?? 'Erro ao cadastrar.';
      this.notif.error(msg);
    } finally {
      this.enviando = false;
    }
  }

  private validar(): boolean {
    if (this.grupos.length === 0) {
      this.notif.warning('Adicione ao menos um grupo.');
      return false;
    }

    for (let i = 0; i < this.grupos.length; i++) {
      const g = this.grupos[i];
      const tag = `Grupo ${i + 1}`;

      if (!g.cnpjEmissor) {
        this.notif.warning(`${tag}: preencha CNPJ Emissor.`);
        return false;
      }
      // CNAE: obrigatorio so no fluxo normal NFSe. Oculto dispensa porque
      // o produto vem direto. CTe tambem dispensa (TES selecionada pelo
      // usuario via planilha REGRAS CTE, produto via U_PnfPrdCte).
      const todosCte = g.documentos.length > 0 && g.documentos.every(d => d.especie === 'CTE');
      if (!g.fornecedorOculto && !todosCte && !g.servicoCnae) {
        this.notif.warning(`${tag}: preencha Servico (CNAE).`);
        return false;
      }
      if (!g.filialCnpj) {
        this.notif.warning(`${tag}: selecione a Filial.`);
        return false;
      }
      if (!isValidCnpj(g.cnpjEmissor)) {
        this.notif.warning(`${tag}: CNPJ Emissor invalido.`);
        return false;
      }
      if (g.documentos.length === 0) {
        this.notif.warning(`${tag}: adicione ao menos um documento.`);
        return false;
      }
      for (let j = 0; j < g.documentos.length; j++) {
        const d = g.documentos[j];
        const dtag = `${tag}, doc ${j + 1}`;
        if (!d.numeroNf || !d.serie || !d.emissao) {
          this.notif.warning(`${dtag}: preencha Numero NF, Serie e Emissao.`);
          return false;
        }
        // Valor robusto: po-decimal as vezes deixa valor null ao sair do
        // foco; o parser de PDF pode retornar 0 quando layout nao casa.
        // Garante number finito > 0 antes de enviar - evita "Falha ao
        // montar itens" no backend.
        const nVal = typeof d.valor === 'number' ? d.valor : parseFloat(String(d.valor ?? ''));
        if (!isFinite(nVal) || nVal <= 0) {
          this.notif.warning(`${dtag}: Valor obrigatorio e maior que zero (atual: ${d.valor ?? 'vazio'}).`);
          return false;
        }
        // Pedagio (CTe) >= valor total -> backend zera o item. Avisa aqui
        // pra o operador corrigir antes de submeter.
        const nPed = typeof d.valorPedagio === 'number' ? d.valorPedagio : 0;
        if (d.especie === 'CTE' && nPed > 0 && nPed >= nVal) {
          this.notif.warning(`${dtag}: Pedagio (${nPed}) maior ou igual ao Valor (${nVal}). Revise os campos.`);
          return false;
        }
        if (g.fornecedorOculto) {
          // Oculto: o usuario informa diretamente os 4 campos contabeis.
          if (!d.contaContabil || !d.bu || !d.centroCusto || !d.produto) {
            this.notif.warning(`${dtag}: para fornecedor oculto preencha Conta Contabil, BU, Centro de Custo e Produto.`);
            return false;
          }
        } else {
          // Fluxo normal: processo obrigatorio + nao encerrado.
          if (!d.processo?.trim()) {
            this.notif.warning(`${dtag}: Processo obrigatorio para fornecedor nao oculto.`);
            return false;
          }
          if (d.processoEncerrado) {
            const dt = d.dataEncerramentoProcesso
              ? ` (encerrado em ${this.formatIsoBr(d.dataEncerramentoProcesso)})`
              : '';
            this.notif.error(`${dtag}: Processo ${d.processo} encerrado${dt}.`);
            return false;
          }
          // CTe: TES eh manual (planilha REGRAS CTE), exige selecao.
          if (d.especie === 'CTE' && !d.tipoOperacao) {
            this.notif.warning(`${dtag}: selecione o Tipo de Operacao (TES) do CTe.`);
            return false;
          }
          // CTe: serie real e chave de acesso (44 dig + DV mod-11) obrigatorias.
          if (d.especie === 'CTE') {
            if (!d.serie || !/^\d{1,3}$/.test(d.serie)) {
              this.notif.warning(`${dtag}: Serie do CTe obrigatoria (1 a 3 digitos).`);
              return false;
            }
            if (!isChaveDacteValida(d.chaveAcesso)) {
              this.notif.warning(`${dtag}: Chave de acesso do CTe invalida ou ausente (44 digitos com DV).`);
              return false;
            }
          }
        }
        // Anexo da NF (Danfe/CTe) obrigatorio ao entregar - regra cliente 2026-07.
        if (!d.danfeFile) {
          this.notif.warning(`${dtag}: Anexo da NF (Danfe/CTe) e obrigatorio.`);
          return false;
        }
        // Boleto obrigatorio: regra cliente 2026-05-29 (refinada). So
        // aplica quando doc.fin=true AND tipoProcessoResolvido='terceiros'
        // (conta e ordem) ou 'encomenda'. Fornecedor oculto / propria /
        // sem processo nao exigem boleto.
        const exigeBoleto = d.fin
          && (d.tipoProcessoResolvido === 'terceiros'
              || d.tipoProcessoResolvido === 'encomenda');
        if (exigeBoleto && !d.boletoFile) {
          this.notif.warning(
            `${dtag}: Anexo Boleto eh obrigatorio quando Gera Financeiro = Sim ` +
            `em processo Conta e Ordem ou Encomenda.`
          );
          return false;
        }
      }
    }

    return true;
  }

  private async uploadAnexos(doc: DocumentoForm, chave: { filial: string; numero: string; serie: string; fornec: string; loja: string }, gedFolder?: string, cnae?: string): Promise<boolean> {
    const arquivos: { tipo: AnexoTipo; file: File }[] = [];
    // O slot "danfe" carrega o documento principal. Para CTe esse documento
    // eh o DACTE: precisa ir com tipo 'CTE' (nao 'DAN') pro backend classificar
    // no GED/Conexos como CTE (file_cod 60 / "FRETE NACIONAL - CTE") e gravar
    // ZOF_TIPO='CTE'. Sem isso o backend caia no fallback "DESPESAS DIVERSAS".
    // Espelha executar-lote.uploadAnexos.
    if (doc.danfeFile) {
      const tipoDoc: AnexoTipo = doc.especie === 'CTE' ? 'CTE' : 'DAN';
      arquivos.push({ tipo: tipoDoc, file: doc.danfeFile });
    }
    if (doc.boletoFile)   arquivos.push({ tipo: 'BOL', file: doc.boletoFile });
    if (doc.romaneioFile) arquivos.push({ tipo: 'ROM', file: doc.romaneioFile });

    const falhas: string[] = [];

    for (const a of arquivos) {
      try {
        const base64 = await this.anexosService.fileToBase64(a.file);
        await firstValueFrom(this.anexosService.upload({
          chave: {
            filOri: chave.filial,
            numero:    chave.numero,
            serie:  chave.serie,
            fornec: chave.fornec,
            loja:   chave.loja
          },
          tipo:        a.tipo,
          filename:    a.file.name,
          contentType: a.file.type || 'application/octet-stream',
          base64,
          gedFolder,
          cnae,
          dtVencimento: toIsoDateLocal(doc.vencimento) || undefined
        }));
      } catch (err: any) {
        // SEM rollback destrutivo: a nota JA foi cadastrada (SF1/SE2 efetivadas).
        // Excluir a SF1 por causa de um anexo podia falhar em BAIXAPARC e deixar
        // a nota orfa/inconsistente. Anexo e' auxiliar: registra a falha, segue
        // com os demais e o operador reenvia pelo detalhe.
        const msg = err?.error?.message ?? 'falha upload';
        falhas.push(`${a.tipo}: ${msg}`);
      }
    }

    if (falhas.length > 0) {
      // status 'ok' de proposito: a NOTA foi criada (nao re-adicionar, evita
      // duplicidade). A mensagem sinaliza os anexos pendentes para reenvio.
      doc.status   = 'ok';
      doc.mensagem = `Nota criada. Anexo(s) com falha: ${falhas.join('; ')} - ` +
                     `reenvie pelo detalhe (NAO re-adicione a nota).`;
      return false;
    }
    return true;
  }

  voltar(): void {
    this.router.navigate(['/notas-list']);
  }
}
